import { ElementRef, Injectable, signal } from '@angular/core';
import { SearchService } from './search.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { ProjectService } from '@core/services/project/project.service';
import { AuthService } from '@core/services/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ActionsService {
  constructor(
    private _search: SearchService,
    private messageService: MessageService,
    private _project: ProjectService,
    private _auth: AuthService
  ) {}
  isShowExtractedEntity: boolean = false;

  // download file
  downloadFile = (data: any) => {
    if (this._project.currentProject().isAllowDownload === false) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Downloading not allowed',
        detail: 'Downloading is not allowed for this project.',
        key: 'tr',
      });
      return;
    }
    if (!data._source.filePath) {
      return;
    }
    let filePath = data._source.filePath;
    if (
      this._auth.appConfig().downloadPathAddCheck &&
      this._auth.appConfig().downloadPathAddCheck[
        this._project.currentProject().name
      ]
    ) {
      let checkValue =
        this._auth.appConfig().downloadPathAddCheck[
          this._project.currentProject().name
        ];
      if (!filePath.startsWith(checkValue)) {
        filePath = checkValue + `/` + filePath;
      }
    }
    let param = {
      filePath: filePath,
    };
    this.messageService.clear();
    this.messageService.add({
      severity: 'info',
      summary: 'Downloading',
      detail: 'Downloading file...',
      key: 'tr',
    });
    this._search.downloadDocFile(param).subscribe({
      next: (response) => {
        let blob = new Blob([response], { type: 'application/octet-stream' });
        let url = window.URL.createObjectURL(blob);
        let a = document.createElement('a');
        a.href = url;
        a.download = data._source.filePath.split('/').pop();
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        this.messageService.clear();
        this.messageService.add({
          severity: 'success',
          summary: 'Downloaded',
          detail: 'File downloaded successfully',
          key: 'tr',
        });
      },
      error: (error) => {
        let errorMessage = '';
        if (
          error.error instanceof Blob &&
          error.error.type === 'application/json'
        ) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errObj = JSON.parse(reader.result as string);
              errorMessage = errObj.message || '';
            } catch (e) {
              errorMessage = 'Unknown error';
            }
            this.messageService.add({
              severity: 'warn',
              summary: error.statusText || 'Something went wrong',
              detail: errorMessage || 'Error while downloading file',
              key: 'tr',
            });
          };
          reader.readAsText(error.error);
        } else {
          errorMessage = error.message || 'Unknown error';
          this.messageService.add({
            severity: 'warn',
            summary: error.statusText || 'Something went wrong',
            detail: errorMessage || 'Error while downloading file',
            key: 'tr',
          });
        }
      },
    });
  };

  // manage Close of selected file
  closeCurrentFile = () => {
    this._search.selectedDocumentSubject.next(null);
    this._search.activeIndex = 0;
  };

  // manage labels
  isShowManageLabels: boolean = false;
  selectedActionLabel = new Subject<any>();
  selectedActionLabelData: any = null;
  isAddingLabel: boolean = false;
  currentModule: 'search' | 'reports-iq' | 'claims' = 'search';
  manageLabels = (data: any, module: 'search' | 'reports-iq' | 'claims' = 'search') => {
    this.isShowManageLabels = true;
    this.currentModule = module;

    // Convert to standardized format based on module
    const standardizedData = this.convertToStandardizedFormat(data, module);

    this.selectedActionLabel.next(standardizedData);
    this.selectedActionLabelData = data; // Keep original for backward compatibility
    this.isAddingLabel = false;
  };

  private convertToStandardizedFormat(data: any, module: 'search' | 'reports-iq' | 'claims') {
    switch (module) {
      case 'search':
        // Search module has _source structure

        const id = data._id || data._source?.id || data._source?._id || data.id;
        if (!id) {
          console.error('No valid id found in data for search module. Expected data._id or data._source.id to be present.');
        }
        return {
          id: id,
          currentLabels: data._source?.labels || [],
          module: 'search' as const,
          index: data._index, // Include index for search module
          metadata: data
        };

      case 'reports-iq':
        // Reports-IQ module: needs summaryTableName for API calls
        return {
          id: data.reference_id,  // Reports-IQ always uses reference_id
          currentLabels: data.labels || [],
          module: 'reports-iq' as const,
          metadata: {
            ...data,
            // Ensure summaryTableName is available from parent component
            summaryTableName: data.summaryTableName
          }
        };

      case 'claims':
        // Claims module: needs claimId for API calls
        return {
          id: data.claimId || data._id || data.id,
          currentLabels: data.labels || [],
          module: 'claims' as const,
          metadata: data
        };

      default:
        // Fallback
        return {
          id: data.id || data._id,
          currentLabels: data.labels || [],
          module: module,
          metadata: data
        };
    }
  }

  closeLabels = () => {
    this.isShowManageLabels = false;
    this.selectedActionLabelData = null;
    this.selectedActionLabel.next(null); // Explicitly emit null to trigger reset
    this.isAddingLabel = false;
    this.currentModule = 'search';
  };

  // manage Share Document
  isShowShareDocument: boolean = false;
  selectedShareDocument = signal(<any>{});

  shareDocument = (data: any) => {
    this.isShowShareDocument = true;
    this.selectedShareDocument.set(data);
  };

  closeShareDocument = () => {
    this.isShowShareDocument = false;
    this.selectedShareDocument.set({} as any);
  };

  // copy to clipboard
  copyToClipboard = (data: any) => {
    navigator.clipboard.writeText(data).then(
      () => {},
      (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed',
          detail: 'Failed to copy to clipboard',
          key: 'tr',
        });
      }
    );
  };

  // addToAiContext
  askCopySelection = new Subject<selection>();
  addToAiContext = (context: string) => {
    this.askCopySelection.next({
      context: context,
      isExplain: false,
    });
  };
  addToAiContextAndExplain = (context: string) => {
    this.askCopySelection.next({
      context: context,
      isExplain: true,
    });
  };
}
export interface selection {
  context : string;
  isExplain : boolean;
}
